/**
 * Accounts desk — pages beyond Counter + Receipts.
 *
 *   Petty cash (real, backed by petty_cash_entry / migration 029)
 *   Reconciliation (pure-client paste-and-match against receipts)
 *   Reports (links to CSV exports + head-wise breakdown from listReceipts)
 *
 * Vendor Bills + Defaulters are re-mounted straight from the admin surface —
 * they already implement the full flow.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert, Button, Card, DatePicker, Empty, Form, Input, InputNumber, Modal, Select,
  Skeleton, Space, Table, Tabs, Tag, Typography, message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import type {
  CreatePettyCashRequest, PettyCashCategory, PettyCashRow, ReceiptRow,
} from '@velora/contracts';
import { ApiError, productApi } from '../../api';

function paise(n: number): string {
  return (n / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}
function today(): string { return new Date().toISOString().slice(0, 10); }

/* ---------------- Petty cash ---------------- */

const CATEGORIES: PettyCashCategory[] = ['stationery', 'tea', 'repairs', 'travel', 'postage', 'other'];
const CATEGORY_TONE: Record<PettyCashCategory, string> = {
  stationery: 'blue', tea: 'gold', repairs: 'volcano', travel: 'geekblue', postage: 'green', other: 'default',
};

export function AccountsPettyCashPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [from, setFrom] = useState<string | undefined>(undefined);
  const [to, setTo] = useState<string | undefined>(undefined);
  const [addOpen, setAddOpen] = useState(false);
  const q = useQuery({
    queryKey: ['petty-cash', institution, from, to],
    queryFn: () => productApi.listPettyCash(institution, from, to),
    enabled: !!institution,
  });
  const voidM = useMutation({
    mutationFn: (input: { id: string; reason: string }) => productApi.voidPettyCash(institution, input.id, { reason: input.reason }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['petty-cash', institution] });
      void message.success('Voided');
    },
  });

  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 6 }} />;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>Petty cash</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            {q.data.total} entr{q.data.total === 1 ? 'y' : 'ies'} · ₹{paise(q.data.totals.active_paise)} active
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>New entry</Button>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <Input placeholder="from yyyy-mm-dd" value={from ?? ''} onChange={(e) => setFrom(e.target.value || undefined)}
          style={{ width: 160, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} size="small" />
        <Input placeholder="to yyyy-mm-dd" value={to ?? ''} onChange={(e) => setTo(e.target.value || undefined)}
          style={{ width: 160, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} size="small" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
        {CATEGORIES.map((c) => (
          <Card key={c} size="small">
            <div style={{ fontSize: 11, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{c}</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>₹{paise(q.data.by_category[c] ?? 0)}</div>
          </Card>
        ))}
      </div>

      <Card size="small">
        {q.data.rows.length === 0 ? (
          <Empty description="No entries in this range." style={{ padding: 20 }} />
        ) : (
          <Table<PettyCashRow>
            size="small" pagination={{ pageSize: 20 }} rowKey="id"
            dataSource={q.data.rows}
            columns={[
              { title: 'Date', dataIndex: 'spent_on', key: 'd', width: 110 },
              { title: 'Category', dataIndex: 'category', key: 'c', width: 120,
                render: (v: PettyCashCategory) => <Tag color={CATEGORY_TONE[v]}>{v}</Tag> },
              { title: 'Description', dataIndex: 'description', key: 'desc',
                render: (v: string, r) => <>
                  {v}
                  {r.spent_by && <div style={{ fontSize: 11, color: '#8a94a6' }}>by {r.spent_by}</div>}
                </> },
              { title: 'Amount', dataIndex: 'amount_paise', key: 'a', align: 'right', width: 120,
                render: (v: number) => <b>₹{paise(v)}</b> },
              { title: '', key: 'act', width: 130,
                render: (_, r) => r.voided_at
                  ? <Tag color="default">voided</Tag>
                  : <VoidButton onVoid={(reason) => voidM.mutate({ id: r.id, reason })} />,
              },
            ]}
          />
        )}
      </Card>

      <NewPettyCashModal open={addOpen} institution={institution} onClose={() => setAddOpen(false)} />
    </Space>
  );
}

function VoidButton({ onVoid }: { onVoid: (reason: string) => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  return (
    <>
      <Button size="small" danger onClick={() => setOpen(true)}>Void</Button>
      <Modal
        open={open}
        title="Void petty cash entry"
        onCancel={() => setOpen(false)}
        okText="Void"
        onOk={() => { if (reason.trim().length >= 3) { onVoid(reason.trim()); setOpen(false); setReason(''); } }}
      >
        <Alert type="warning" showIcon message="Voided entries stay on the record — they never disappear." style={{ marginBottom: 12 }} />
        <Input.TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" />
      </Modal>
    </>
  );
}

interface PettyCashForm {
  spent_on: Dayjs;
  category: PettyCashCategory;
  amount_rupees: number;
  description: string;
  spent_by?: string;
}

function NewPettyCashModal({
  open, institution, onClose,
}: { open: boolean; institution: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [form] = Form.useForm<PettyCashForm>();
  const m = useMutation({
    mutationFn: (input: CreatePettyCashRequest) => productApi.createPettyCash(institution, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['petty-cash', institution] });
      form.resetFields();
      void message.success('Saved');
      onClose();
    },
  });
  const err = m.error instanceof ApiError
    ? ((m.error.body as { message?: string } | null)?.message ?? null)
    : m.error instanceof Error ? m.error.message : null;

  return (
    <Modal
      open={open} title="New petty cash entry"
      onCancel={() => { m.reset(); onClose(); }}
      okText="Save"
      confirmLoading={m.isPending}
      onOk={() => form.submit()}
      destroyOnClose
    >
      <Form
        form={form} layout="vertical"
        initialValues={{ category: 'stationery' as PettyCashCategory }}
        onFinish={(v) => m.mutate({
          spent_on: v.spent_on.format('YYYY-MM-DD'),
          amount_paise: Math.round(v.amount_rupees * 100),
          category: v.category,
          description: v.description.trim(),
          spent_by: v.spent_by?.trim() || undefined,
        })}
      >
        <Form.Item name="spent_on" label="Date" rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="category" label="Category" rules={[{ required: true }]}>
          <Select options={CATEGORIES.map((c) => ({ value: c, label: c }))} />
        </Form.Item>
        <Form.Item name="amount_rupees" label="Amount (₹)" rules={[{ required: true, type: 'number', min: 1 }]}>
          <InputNumber style={{ width: '100%' }} min={1} step={10} />
        </Form.Item>
        <Form.Item name="description" label="Description" rules={[{ required: true, min: 1, max: 300 }]}>
          <Input placeholder="e.g. Chart paper for wall display" />
        </Form.Item>
        <Form.Item name="spent_by" label="Spent by (optional)">
          <Input placeholder="Staff name" />
        </Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

/* ---------------- Reconciliation ---------------- */

export function AccountsReconciliationPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [raw, setRaw] = useState('');

  const receipts = useQuery({
    queryKey: ['receipts', institution, from, to],
    queryFn: () => productApi.listReceipts(institution, { from, to }),
    enabled: !!institution,
  });

  const bankLines = useMemo(() => parseBankLines(raw), [raw]);
  const rowsSource: ReceiptRow[] = receipts.data?.rows ?? [];
  const { matched, unmatchedBank, unmatchedReceipts } = matchLines(bankLines, rowsSource);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Reconciliation</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          Paste bank statement lines below, then compare against receipts in the range. Matching is amount-based (with optional receipt-number hint).
        </Typography.Text>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <Input placeholder="from yyyy-mm-dd" value={from} onChange={(e) => setFrom(e.target.value)}
          style={{ width: 160, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} size="small" />
        <Input placeholder="to yyyy-mm-dd" value={to} onChange={(e) => setTo(e.target.value)}
          style={{ width: 160, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} size="small" />
      </div>

      <Card size="small" title="Bank statement lines">
        <Alert type="info" showIcon style={{ marginBottom: 8 }}
          message="One line per transaction — amount required, receipt no. optional"
          description={<span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>2025-09-10, 1500, RCP-2025-000123 (from ICICI reversal)</span>} />
        <Input.TextArea
          rows={6}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder={"2025-09-10, 1500, RCP-2025-000123\n2025-09-10, 2500\n..."}
        />
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        <MatchCard title={`Matched (${matched.length})`} tone="ok" empty="Nothing matched yet.">
          {matched.map((m, i) => (
            <div key={i} style={rowStyle}>
              <b>₹{paise(m.receipt.amount_paise)}</b>
              <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{m.receipt.receipt_number}</span>
              <div style={{ flex: 1 }} />
              <span style={{ fontSize: 11, color: '#8a94a6' }}>{m.bank.date}</span>
            </div>
          ))}
        </MatchCard>
        <MatchCard title={`Bank not in receipts (${unmatchedBank.length})`} tone="warn" empty="Every bank line matched.">
          {unmatchedBank.map((b, i) => (
            <div key={i} style={rowStyle}>
              <b>₹{paise(b.amount_paise)}</b>
              <span style={{ fontSize: 11, color: '#8a94a6' }}>{b.date}</span>
              {b.ref && <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{b.ref}</span>}
            </div>
          ))}
        </MatchCard>
        <MatchCard title={`Receipts not in bank (${unmatchedReceipts.length})`} tone="warn" empty="Every receipt is in the bank.">
          {unmatchedReceipts.map((r) => (
            <div key={r.id} style={rowStyle}>
              <b>₹{paise(r.amount_paise)}</b>
              <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{r.receipt_number}</span>
              <span style={{ fontSize: 11, color: '#8a94a6' }}>{r.method}</span>
            </div>
          ))}
        </MatchCard>
      </div>
    </Space>
  );
}

function MatchCard({
  title, tone, children, empty,
}: { title: string; tone: 'ok' | 'warn'; empty: string; children: React.ReactNode }) {
  const kids = Array.isArray(children) ? children : [children];
  return (
    <Card size="small" title={<span style={{ color: tone === 'warn' ? '#a24040' : '#1c6442' }}>{title}</span>}>
      {kids.length === 0 ? <Empty description={empty} style={{ padding: 12 }} /> : <div>{children}</div>}
    </Card>
  );
}
const rowStyle: React.CSSProperties = {
  padding: '6px 0', borderTop: '1px solid #f1f1f4', display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 13,
};

interface BankLine { date: string; amount_paise: number; ref: string | null; }
function parseBankLines(raw: string): BankLine[] {
  const out: BankLine[] = [];
  for (const line of raw.split('\n')) {
    const parts = line.split(',').map((s) => s.trim()).filter(Boolean);
    if (parts.length < 2) continue;
    const rupees = Number(parts[1]);
    if (!Number.isFinite(rupees) || rupees <= 0) continue;
    out.push({
      date: parts[0] ?? '',
      amount_paise: Math.round(rupees * 100),
      ref: parts[2] ?? null,
    });
  }
  return out;
}
function matchLines(bank: BankLine[], receipts: ReceiptRow[]): {
  matched: Array<{ bank: BankLine; receipt: ReceiptRow }>;
  unmatchedBank: BankLine[];
  unmatchedReceipts: ReceiptRow[];
} {
  const usedReceipts = new Set<string>();
  const matched: Array<{ bank: BankLine; receipt: ReceiptRow }> = [];
  const unmatchedBank: BankLine[] = [];
  for (const b of bank) {
    // Prefer receipt-number match, then amount+date, then amount alone.
    let hit = b.ref ? receipts.find((r) => r.receipt_number === b.ref && !usedReceipts.has(r.id)) : undefined;
    if (!hit) hit = receipts.find((r) => r.amount_paise === b.amount_paise && r.received_on === b.date && !usedReceipts.has(r.id));
    if (!hit) hit = receipts.find((r) => r.amount_paise === b.amount_paise && !usedReceipts.has(r.id));
    if (hit) {
      usedReceipts.add(hit.id);
      matched.push({ bank: b, receipt: hit });
    } else {
      unmatchedBank.push(b);
    }
  }
  const unmatchedReceipts = receipts.filter((r) => !usedReceipts.has(r.id));
  return { matched, unmatchedBank, unmatchedReceipts };
}

/* ---------------- Reports ---------------- */

export function AccountsReportsPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const receipts = useQuery({
    queryKey: ['receipts', institution, from, to, 'reports'],
    queryFn: () => productApi.listReceipts(institution, { from, to }),
    enabled: !!institution,
  });
  const rows = receipts.data?.rows ?? [];

  const byHead = groupBy(rows, (r) => r.fee_head_name);
  const byMethod = groupBy(rows, (r) => r.method);
  const byDate = groupBy(rows, (r) => r.received_on);

  const csvUrls = {
    receipts: productApi.exportUrl(institution, 'receipts', { from, to }),
    students: productApi.exportUrl(institution, 'students'),
    attendance: productApi.exportUrl(institution, 'attendance', { from, to }),
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ margin: 0 }}>Reports</Typography.Title>

      <div style={{ display: 'flex', gap: 8 }}>
        <Input placeholder="from yyyy-mm-dd" value={from} onChange={(e) => setFrom(e.target.value)}
          style={{ width: 160, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} size="small" />
        <Input placeholder="to yyyy-mm-dd" value={to} onChange={(e) => setTo(e.target.value)}
          style={{ width: 160, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} size="small" />
      </div>

      <Card size="small" title="CSV exports">
        <Space wrap size={8}>
          <a href={csvUrls.receipts}><Button>Receipts (date range)</Button></a>
          <a href={csvUrls.students}><Button>Students</Button></a>
          <a href={csvUrls.attendance}><Button>Attendance (date range)</Button></a>
        </Space>
      </Card>

      <Tabs
        items={[
          { key: 'head', label: 'By fee head', children: <BreakdownTable data={byHead} col="Head" /> },
          { key: 'method', label: 'By method', children: <BreakdownTable data={byMethod} col="Method" /> },
          { key: 'date', label: 'By day', children: <BreakdownTable data={byDate} col="Date" /> },
        ]}
      />
    </Space>
  );
}

function BreakdownTable({ data, col }: { data: Map<string, { count: number; sum: number }>; col: string }) {
  const rows = Array.from(data.entries()).map(([label, v]) => ({ label, ...v }));
  rows.sort((a, b) => b.sum - a.sum);
  return (
    <Table
      size="small" pagination={false} rowKey="label"
      dataSource={rows}
      columns={[
        { title: col, dataIndex: 'label', key: 'l' },
        { title: '#', dataIndex: 'count', key: 'c', align: 'right', width: 70 },
        { title: 'Amount', dataIndex: 'sum', key: 's', align: 'right',
          render: (v: number) => <b>₹{paise(v)}</b> },
      ]}
    />
  );
}
function groupBy<T>(list: T[], key: (t: T) => string): Map<string, { count: number; sum: number }> {
  const m = new Map<string, { count: number; sum: number }>();
  for (const r of list) {
    const k = key(r);
    const cur = m.get(k) ?? { count: 0, sum: 0 };
    cur.count += 1;
    cur.sum += (r as unknown as { amount_paise: number }).amount_paise;
    m.set(k, cur);
  }
  return m;
}
function monthStart(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`;
}
